import { AxiosError, InternalAxiosRequestConfig } from "axios";

export interface AxiosAuthError {
  status: number;
  data?: {
    status?: string;
    data?: {
      message?: string;
    };
  };
}

export interface RefreshData {
  auth: {
    token?: string;
    expiresAt?: string | number;
  };
  refresh: {
    token?: string;
    expiresAt?: string | number;
  };
}

type ErrorInput = {
  data?: { msg?: string; message?: string };
  statusText?: string;
};

type CleanErrorInput = {
  data?: any;
  status: number;
  statusText?: string;
  original?: unknown;
};

export const parseStoredTokenExpiry = (tokenExpiry?: string): Date | undefined => {
  const raw = tokenExpiry;
  if (typeof raw === "undefined" || raw === "") return undefined;
  const exp = new Date(raw);
  if (Number.isNaN(exp.getTime())) return undefined;
  return exp;
};

export const isAccessTokenExpiringWithinMs = (
  ms: number,
  tokenExpiry?: string
): boolean => {
  const exp = parseStoredTokenExpiry(tokenExpiry);
  if (exp === undefined) return false;
  const boundary = new Date(exp.getTime() - ms);
  const now = new Date();
  return now >= boundary;
};

export const clearStoredAuthIfInvalidRefresh = (
  err: any,
  storageKeys: {
    refreshToken: string;
    authToken: string;
    tokenExpiry: string;
    refreshExpiry: string;
  }
) => {
  if (err?.data?.data?.message === "invalid refresh token") {
    localStorage.removeItem(storageKeys.refreshToken);
    localStorage.removeItem(storageKeys.authToken);
    localStorage.removeItem(storageKeys.tokenExpiry);
    localStorage.removeItem(storageKeys.refreshExpiry);
  }
};

export const setAuthHeader = (
  config: InternalAxiosRequestConfig,
  authTokenKey: string
) => {
  const token = localStorage.getItem(authTokenKey);
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
};

export const hasJWTExpired = (err: AxiosAuthError) => {
  return (
    (err.status === 401 && err.data?.data?.message === "jwt expired") ||
    (err.status === 200 && err.data?.status === "jwt expired")
  );
};

export const getErrorMessage = (error: ErrorInput) => {
  if (error.data?.msg) {
    return error.data.msg;
  }
  if (error.data?.message) {
    return error.data.message;
  }
  return error.statusText;
};

export const cleanErrorObject = (error: CleanErrorInput) => {
  return {
    data: error.data,
    status: error.status,
    statusText: error.statusText,
    message: getErrorMessage(error),
    original: error.original ?? error,
  };
};

export const cleanErrorObjectFromAxiosError = (error: AxiosError) => {
  const r = error.response;
  return cleanErrorObject({
    data: r?.data,
    status: r?.status ?? 0,
    statusText: r?.statusText,
    original: error,
  });
};

export const toAxiosAuthPayload = (error: AxiosError): AxiosAuthError => ({
  status: error.response?.status ?? 0,
  data: error.response?.data as AxiosAuthError["data"],
});
